import React from "react";
import { Modal } from "react-bootstrap";

import { useHouseDispatch } from "../../contexts/HouseContext";
import { PAYMENT_METHODS, MethodGlyph } from "./components/paymentMethods";
import "./LedgerModal.css";

/**
 * Add a transaction for a player. Records a buy-in, and — when
 * `allowCashOut` is set (the web ledger) — a plain cash-out as well. The
 * mobile ledger leaves `allowCashOut` off and routes cash-outs through the
 * dedicated guided flow instead.
 */
export const AddTransactionModal = ({ game, player, show, handleClose, allowCashOut = false }) => {
    const actions = useHouseDispatch();

    if (!player || !show) return null;

    const pendingTxn = game.pendingTransaction;
    const isCashOut = allowCashOut && pendingTxn.transactionType === "CASH_OUT";

    const updatePending = (patch) =>
        actions.updatePendingTransaction(game.id, { ...pendingTxn, ...patch });

    const handlePaymentTypeClick = (type) => () =>
        updatePending({ paymentType: type, isSettled: type === "CASH" });

    const setTransactionType = (type) => () => updatePending({ transactionType: type });

    const handleAmountChange = (e) => updatePending({ amount: e.target.value });

    const toggleSettled = () => updatePending({ isSettled: !pendingTxn.isSettled });

    const handleSubmit = async (e) => {
        e.preventDefault();
        const result = await actions.createTransaction(game.id, player.id, {
            paymentType: pendingTxn.paymentType,
            isSettled: pendingTxn.isSettled,
            type: isCashOut ? "CASH_OUT" : "BUY_IN",
            amount: parseFloat(pendingTxn.amount),
        });

        if (result.error) {
            console.log("transaction submit failed", { result });
            return;
        }

        handleClose();
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            dialogClassName="lm-dialog"
            backdropClassName="lm-backdrop"
            centered
        >
            <form onSubmit={handleSubmit}>
                <div className="lm-head">
                    <div className="lm-head-l">
                        <div className="lm-kicker">
                            {allowCashOut ? "New transaction" : "New buy-in"}
                        </div>
                        <h2 className="lm-title">
                            Add for <em>{player.name}</em>
                        </h2>
                    </div>
                    <button
                        type="button"
                        className="lm-close"
                        aria-label="Close"
                        onClick={handleClose}
                    >
                        ×
                    </button>
                </div>

                <div className="lm-body">
                    {allowCashOut && (
                        <div className="lm-section">
                            <span className="lm-l">Type</span>
                            <div className="bc-toggle">
                                <button
                                    type="button"
                                    className={`bc-opt${isCashOut ? "" : " is-on"}`}
                                    onClick={setTransactionType("BUY_IN")}
                                >
                                    <span className="bc-glyph">+</span>
                                    Buy in
                                </button>
                                <button
                                    type="button"
                                    className={`bc-opt cashout${isCashOut ? " is-on" : ""}`}
                                    onClick={setTransactionType("CASH_OUT")}
                                >
                                    <span className="bc-glyph">↗</span>
                                    Cash out
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="lm-section">
                        <span className="lm-l">Payment method</span>
                        <div className="pay-grid">
                            {PAYMENT_METHODS.map((m) => (
                                <button
                                    key={m.type}
                                    type="button"
                                    className={`pay-tile${pendingTxn.paymentType === m.type ? " is-on" : ""}`}
                                    onClick={handlePaymentTypeClick(m.type)}
                                >
                                    <div className="pay-tile-glyph">
                                        <MethodGlyph type={m.type} />
                                    </div>
                                    <div className="pay-tile-name">{m.label}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="lm-section">
                        <span className="lm-l">Amount</span>
                        <label className="amt-input-wrap">
                            <span className="currency">$</span>
                            <input
                                type="text"
                                inputMode="decimal"
                                name="amount"
                                className="amt-input"
                                placeholder="0"
                                value={pendingTxn.amount || ""}
                                onChange={handleAmountChange}
                                autoFocus
                            />
                        </label>
                    </div>

                    <div className="lm-section">
                        <button
                            type="button"
                            className="settled-row"
                            onClick={toggleSettled}
                        >
                            <div className="settled-label">
                                Settled
                                <span className="desc">
                                    Money has changed hands. Toggle off to record an IOU.
                                </span>
                            </div>
                            <span
                                className={`lm-toggle${pendingTxn.isSettled ? " is-on" : ""}`}
                                aria-hidden="true"
                            />
                        </button>
                    </div>
                </div>

                <div className="lm-foot">
                    <span className="lm-foot-meta">⌘ + Enter to submit</span>
                    <div className="lm-foot-actions">
                        <button
                            type="button"
                            className="lm-cancel"
                            onClick={handleClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`lm-submit${isCashOut ? " is-cashout" : ""}`}
                        >
                            {isCashOut ? "Add cash-out" : "Add buy-in"}
                        </button>
                    </div>
                </div>
            </form>
        </Modal>
    );
};
